<template>
  <div class="sm:col-span-2">
    <dt class="flex items-baseline justify-between font-medium text-gray-500 text-md">
      {{ overwriteLabel ? overwriteLabel : $t("Attachments") }}

      <Menu v-if="!disabled"
            v-slot="{ close }"
            as="div"
            class="relative flex-shrink-0 ml-auto cursor-pointer"
            @keydown.space.prevent>
        <MenuButton class="inline-flex rounded-md shadow-sm cursor-pointer" @click="add_link = false">
          <span
            class="text-sm px-3 inline-flex items-center rounded-md dark:text-common-grey border border-gray-300 dark:border-gray-600 bg-white dark:bg-dark-box-background py-[9px]">
            <Plus class="flex-shrink-0 w-5 h-5 cursor-pointer" />
            {{ $t("Add") }}
          </span>
        </MenuButton>

        <MenuItems
          class="absolute z-50 w-40 text-center origin-bottom-left bg-white border rounded-md focus:outline-none"
          :class="[`${openPosition == 'center' ? '-ml-[45px]' : openPosition == 'left' ? '-ml-[90px]' : ''}`]"
          @keydown.space.prevent>
          <div v-if="!add_link && options.includes('file')" class="h-10 p-2 pb-2 border-b hover:bg-gray-100">
            <div class="h-0 w-0 ml-[40%]">
              {{ $t("File") }}
            </div>
            <input
              name="file-upload"
              type="file"
              :placeholder="$t('here')"
              multiple
              class="custom-file-input w-[200%] -ml-[50%] h-10 cursor-pointer border"
              @change="(e) => addFiles(e, close)">
          </div>

          <div v-if="!add_link && options.includes('link')" class="p-2 hover:bg-gray-100" @click="add_link = true">
            {{ $t("Link") }}
          </div>

          <div>
            <Form
              v-if="add_link"
              v-slot="{ values, handleSubmit }"
              :class="[`${openPosition == 'center' ? '-ml-[90px]' : openPosition == 'left' ? '-ml-[180px]' : ''}`]"
              :validation-schema="schema"
              :initial-values="initial_values"
              class="bg-white dark:bg-dark-box-background p-3 border rounded-lg w-[340px] space-y-2">
              <TextInput type="text"
                         :label="$t('Label')"
                         name="name"
                         @keydown.space.stop
                         @keyup.space.stop
                         @keypress.space.stop />
              <TextInput type="text"
                         :label="$t('Link')"
                         name="link"
                         @keydown.space.stop
                         @keyup.space.stop
                         @keypress.space.stop />
              <div class="flex gap-x-2">
                <button
                  type="button"
                  class="w-1/2 mt-3 mb-3 cancel-btn"
                  @click="
                    () => {
                      add_link = false
                      close()
                    }
                  ">
                  {{ $t("Cancel") }}
                </button>
                <button
                  type="button"
                  class="w-1/2 mt-3 mb-3 primary-btn"
                  @click="
                    () => {
                      handleSubmit(close)
                      addLink(values, close)
                    }
                  ">
                  {{ $t("Add a link") }}
                </button>
              </div>
            </Form>
          </div>
        </MenuItems>
      </Menu>
    </dt>

    <dd v-if="attachments_clone.length" class="mt-1 text-sm text-gray-900">
      <transition-group
        tag="ul"
        role="list"
        mode="in-out"
        name="slide-fade"
        class="border border-gray-200 divide-y divide-gray-200 rounded-md dark:divide-common-grey dark:border-dark-divider-border">
        <li
          v-for="(att, index) in attachments_clone"
          :key="index"
          class="flex items-center justify-between py-3 pl-3 pr-4 text-sm rounded-md hover:bg-gray-200 dark:hover:bg-dark-screen-background hover:cursor-pointer"
          @click.prevent="handleEdit(att, index)">
          <div class="flex items-center flex-1 w-0">
            <Link v-if="att.link" class="flex-shrink-0 w-5 h-5 text-gray-400" />

            <Paperclip v-else class="flex-shrink-0 w-5 h-5 text-gray-400" aria-hidden="true" />
            <span class="flex-1 w-0 ml-2 truncate dark:text-white">{{ att.name }}</span>
          </div>
          <CircleArrowDown
            v-if="att.file && !att.file.name"
            class="rounded-full h-7 w-7 hover:bg-gray-300 dark:hover:bg-transparent hover:cursor-pointer text-common-grey"
            aria-hidden="true"
            @click.stop="openAttachment(att)" />
          <CircleX
            v-if="!disabled"
            class="text-red-400 rounded-full h-7 w-7 hover:bg-red-200 dark:hover:bg-transparent dark:hover:text-red-500 hover:cursor-pointer"
            aria-hidden="true"
            @click.stop="deleteAttachment(att, index)" />
        </li>
      </transition-group>
    </dd>
    <dd v-else class="relative w-full p-4 text-center border rounded-lg top-4 dark:border-dark-divider-border text-common-grey">
      <div class="text-lg">
        {{ $t("No attachments") }}
      </div>
    </dd>
    <Transition name="fade" class="mt-2">
      <span v-if="upload_error" class="float-right py-1 text-red-500">{{ upload_error }}</span>
    </Transition>

    <!-- Edit link -->
    <Modal v-model:is-open="is_edit_on" default-success-msg="Edited">
      <template #form="{ close: closeModal }">
        <h3 class="text-lg font-medium leading-6 text-center text-gray-900 dark:text-dark-body-text">
          {{ $t("Attachment") }} {{ attachments_clone[selected_attachment_index].name }}
        </h3>
        <div class="flex items-center justify-center w-full mt-5 text-center">
          <Form
            v-slot="{ values, handleSubmit }"
            :validation-schema="schema"
            :initial-values="initial_values"
            class="bg-white dark:bg-dark-box-background p-3 border dark:border-dark-divider-border rounded-lg w-[340px] space-y-2"
            @submit="addLink">
            <TextInput type="text"
                       :label="$t('Label')"
                       name="name"
                       @keydown.space.stop
                       @keyup.space.stop
                       @keypress.space.stop />
            <TextInput
              type="text"
              :label="$t('Link')"
              name="link"
              @blur="encodeURIComponent(values.link)"
              @keydown.space.stop
              @keyup.space.stop
              @keypress.space.stop />

            <button type="button" class="cancel-btn" @click="closeModal">{{ $t("Cancel") }}</button>
            <button type="submit" class="mt-3 mb-3 primary-btn" @click="handleSubmit(closeModal)">{{ $t("Save") }}</button>
          </Form>
        </div>
      </template>
    </Modal>
    <input ref="gupload"
           :disabled="disabled"
           name="gupload"
           type="file"
           download
           hidden
           @change="addFile">
  </div>
</template>

<script>
import * as Yup from "yup"
import { ref } from "vue"
import { Form } from "vee-validate"
import { Plus, Link, CircleX, Paperclip, CircleArrowDown } from "lucide-vue-next"
import { Menu, MenuButton, MenuItems } from "@headlessui/vue"
import { utils } from "@/store/helpers"
import TextInput from "@/components/shared/TextInput.vue"
import { useI18n } from "vue-i18n"
import Modal from "@/components/shared/Modal.vue"

const maxFileAllowedSizeMB = 20
const maxFileAllowedSize = maxFileAllowedSizeMB * 1024 * 1024

export default {
  components: {
    Modal,
    TextInput,
    Menu,
    MenuButton,
    MenuItems,
    Form,
    Paperclip,
    Plus,
    CircleX,
    CircleArrowDown,
    Link
  },
  props: {
    overwriteLabel: null,
    modelValue: {
      type: Array,
      default: () => []
    },
    options: {
      type: Array,
      default: () => ["file", "link"]
    },
    disabled: {
      type: Boolean,
      default: false
    },
    openPosition: {
      type: String,
      default: "left"
    }
  },
  emits: ["update:modelValue", "update:deletedAttachments", "update:newAttachments"],
  setup(props) {
    const { t } = useI18n()
    const schema = Yup.object().shape({
      link: Yup.string().url(t("Must be a valid URL")).required(t("link is required")),
      name: Yup.string().nullable()
    })

    const attachments_clone = ref(utils.clone(props.modelValue) || [])
    let initial_values = { name: "", link: "" }

    return {
      initial_values,
      schema,
      attachments_clone
    }
  },
  data() {
    return {
      selected_attachment: null,
      selected_attachment_index: null,
      is_edit_on: false,
      upload_error: null,
      deleted_attachemnts: [],
      new_attachments: [],
      add_link: false
    }
  },

  watch: {
    modelValue() {
      this.attachments_clone = this.modelValue
    },
    attachments_clone: {
      deep: true,
      handler() {
        this.$emit("update:modelValue", this.attachments_clone)
      }
    },
    new_attachments: {
      deep: true,
      handler() {
        this.$emit("update:newAttachments", this.new_attachments)
      }
    },
    deleted_attachemnts: {
      deep: true,
      handler() {
        this.$emit("update:deletedAttachments", this.deleted_attachemnts)
      }
    }
  },
  methods: {
    deleteAttachment(att, index) {
      this.deleted_attachemnts.push(att)
      this.attachments_clone.splice(index, 1)
      this.$emit("update:modelValue", this.attachments_clone)
      let new_indx = this.new_attachments.findIndex((a) => a.name == att.name)
      if (new_indx) {
        this.new_attachments.splice(new_indx, 1)
      }
    },
    openAttachment(att) {
      window.open(att.file, "_blank")
    },
    handleEdit(attachemnt, index) {
      this.selected_attachment_index = index
      this.initial_values = attachemnt
      if (attachemnt.file) {
        this.$refs["gupload"].click()
      } else {
        window.open(
          `${attachemnt.link.includes("https://") || attachemnt.link.includes("http://") ? "" : "http://"}${attachemnt.link}`,
          "_blank"
        )
      }
    },
    addLink(values, close) {
      close()
      this.add_link = false
      utils.replaceOrAddItem(this.attachments_clone, { name: values.name, link: values.link }, "link")
      utils.replaceOrAddItem(this.new_attachments, { name: values.name, link: values.link }, "link")
    },
    addFile(event) {
      this.deleted_attachemnts.push(this.attachments_clone[this.selected_attachment_index])
      this.attachments_clone.splice(this.selected_attachment_index, 1, { name: event.target.files[0].name, file: event.target.files[0] })
      utils.replaceOrAddItem(this.new_attachments, { name: event.target.files[0].name, file: event.target.files[0] }, "name")

      this.selected_attachment_index = null
      this.selected_attachment = null
    },
    addFiles(event, close) {
      close()
      Array.from(event.target.files).map((file) => {
        if (file.size < maxFileAllowedSize) {
          utils.replaceOrAddItem(this.attachments_clone, { name: file.name, file: file }, "name")
          utils.replaceOrAddItem(this.new_attachments, { name: file.name, file: file }, "name")
        } else {
          this.upload_error = this.$t("Max File size is") + ` ${20} ` + this.$t("MB")
          setTimeout(() => {
            this.upload_error = null
          }, 5000)
        }
      })
    }
  }
}
</script>

<style>
.slide-fade-enter-active {
  transition: all 0.3s ease-out;
}

.slide-fade-leave-active {
  transition: all 0.1s;
}

.slide-fade-enter-from,
.slide-fade-leave-to {
  transform: translateY(-20px);
  opacity: 0;
}
</style>
